import { Icon } from "@blueprintjs/core";
import classNames from "classnames";
import { usePrevious } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import { useCommonHooks } from "../hooks";
import { ETLCodes } from "../locales";

export function fileExtension(
  filename: string,
  opts: {
    preserveCase?: boolean;
  } = {}
) {
  if (!filename) return "";
  const ext = (/[^./\\]*$/.exec(filename) || [""])[0];
  return opts.preserveCase ? ext : ext.toLowerCase();
}

export interface DropzoneFile {
  name: string;
  extension: string;
  file: File;
}

const DropZoneContainer = styled.div<{ disabled: boolean }>`
  width: 100%;
  background-color: #eaeaea;
  border: 1px dashed rgb(172, 169, 169);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  cursor: ${(props) => (props.disabled ? "none" : "pointer")};

  &.highlight {
    background-color: rgb(188, 185, 236);
  }
`;

const StyledInput = styled.input`
  position: absolute;
  visibility: hidden;
  display: none !important;
`;

export interface IDropzoneProps {
  disabled?: boolean;
  files?: DropzoneFile[];
  onFilesAdded: (files: DropzoneFile[]) => void;
  onFilesProcessing?: () => void;
  maxFiles?: number;
  className?: string;
  acceptExtensions?: string[];
}

export const Dropzone: React.FunctionComponent<IDropzoneProps> = ({
  disabled = false,
  onFilesAdded,
  onFilesProcessing,
  files,
  maxFiles = 0,
  className,
  acceptExtensions,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>();
  const { t } = useCommonHooks();
  const [highlight, setHighlight] = React.useState(false);

  const openFileDialog = React.useCallback(() => {
    if (disabled) return;
    fileInputRef.current.click();
  }, [disabled]);

  const [fileList, setFileList] = React.useState<FileList>();

  const filesArray = React.useMemo<DropzoneFile[]>(() => {
    if (!fileList) return null;
    const array: File[] = [];
    for (let i = 0; i < fileList.length; i++) {
      array.push(fileList.item(i));
    }
    var res = array.map((f) => ({
      file: f,
      name: f.name,
      extension: fileExtension(f.name),
    }));

    return res;
  }, [fileList]);

  const previousFilesArray = usePrevious(filesArray);

  React.useEffect(() => {
    if (filesArray && previousFilesArray !== filesArray) {
      onFilesAdded(filesArray);
    }
  }, [filesArray, onFilesAdded, previousFilesArray]);

  const onInputChange = React.useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      // if (
      //   canDoAction("UploadFileLimited") &&
      //   evt.target.files[0].size > 10485760
      // ) {
      //   return;
      // }
      const files = evt.target.files;
      if (files?.length > 0) {
        onFilesProcessing?.();
      }
      setFileList(files);
    },
    [disabled, onFilesProcessing]
  );

  const onDragOver = React.useCallback(
    (evt: React.DragEvent<HTMLDivElement>) => {
      evt.preventDefault();
      if (disabled) return;
      setHighlight(true);
    },
    [disabled]
  );

  const onDragLeave = React.useCallback(
    (evt: React.DragEvent<HTMLDivElement>) => {
      evt.preventDefault();
      setHighlight(false);
    },
    []
  );

  const onDrop = React.useCallback(
    (evt: React.DragEvent<HTMLDivElement>) => {
      evt.preventDefault();
      if (disabled) return;
      const files = evt.dataTransfer.files;
      if (files?.length > 0) {
        onFilesProcessing?.();
      }
      setFileList(files);
      setHighlight(false);
    },
    [disabled, onFilesProcessing]
  );

  const uploadedFileNames = React.useMemo(
    () => filesArray?.map((d) => d.name),
    [filesArray]
  );

  const hasFile = React.useMemo(
    () => filesArray?.length > 0,
    [filesArray?.length]
  );

  const finalAcceptExtensions = React.useMemo(
    () => acceptExtensions?.join(",") ?? "",
    [acceptExtensions]
  );

  return (
    <div className={className}>
      <DropZoneContainer
        style={{ height: `${25 + 20 * files?.length ?? 0}px` }}
        onClick={openFileDialog}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={classNames({ highlight })}
        disabled={disabled}
      >
        {!hasFile ? (
          <>
            <Icon
              icon="cloud-upload"
              iconSize={48}
              style={{ opacity: "0.5" }}
            />
            <span>{t(ETLCodes.ClickHereOrDropFile)}</span>
          </>
        ) : (
          <>
            {uploadedFileNames?.map((name, i) => (
              <div key={i}>
                {name.length < 40 ? name : name.slice(0, 40) + "..."}
              </div>
            ))}
          </>
        )}
        <StyledInput
          ref={fileInputRef}
          className="FileInput"
          type="file"
          onChange={onInputChange}
          multiple={true}
          accept={finalAcceptExtensions}
        />
      </DropZoneContainer>
    </div>
  );
};
